import React, { createContext, useContext } from 'react'

import _ from 'lodash'

import { LevelPlaySurfaceProvider } from '@/shared/level-play'
import { SiteMeta } from '@/shared/lib/meta/page-meta'
import { LoadWrapper } from '@/shared/ui'
import type { BlockCondition } from '@/types/blocker'
import type { DigitalProduct } from '@/types/digitalProduct'
import type { Challenge, Journey, Level } from '@/types/journey'
import type { Progress } from '@/types/progress'


import { getConfig } from './config'
import { useLevel } from './useLevel'

import type { UseFormReturn } from 'react-hook-form'

export interface EnrichedLevel extends Level {
  _progress: Progress
  _challenges: Challenge[]
  _lastUnfinishedChallenge?: Challenge
  _allChallengesFinished: boolean
  _finishedPercent: number
  _correctPercent: number
  _blockCondition: BlockCondition
  _levelContinue: boolean
}

export interface LevelContextType {
  challengeCardRef: React.RefObject<HTMLDivElement>
  countdownRef: React.RefObject<{ start: () => void; pause: () => void }>
  digitalProduct: Partial<DigitalProduct>
  levelId: string
  level: EnrichedLevel | null
  journey: Journey | null
  journeyUid: string | undefined
  journeyAllowWrongAnswers: boolean
  journeyBlockRedo: boolean
  hearts: number
  loseHeart: () => void
  isSubmitting: boolean
  setIsSubmitting: (isSubmitting: boolean) => void
  disableControls: boolean
  disableInputs: boolean
  answerIsWrong: boolean
  answerIsRight?: boolean
  answerIsEmpty: boolean
  config: any
  methods: UseFormReturn
  onLevelReset: () => Promise<void>
  onLevelRestart: () => void
  currentScreen: string | null
  goToFinishScreen: () => Promise<void>
  currentStepIndex: number
  steps: any[]
  totalSteps: number
  stepsCustomFooters: Record<string, React.ReactNode>
  setStepsCustomFooters: (stepId: string, customFooter: React.ReactNode) => void
  goToPrevStep: () => void
  goToNextStep: () => void
  goToStepByChallengeId: (challengeId: number | string) => void
  /** Quantos desafios a execução apresenta — pulados não entram na conta. */
  playableChallengesCount: number
  getChallenge: (challengeId: number | string) => Challenge | undefined
  onChallengeVerifyAnswer: (data: {
    uid: string
    answer: any
    answerIsCorrect?: boolean
  }) => Promise<void>
  onChallengeComplete: (data: { answer: any }) => void
  onChallengeContinue: (data: { uid: string; answer: any }) => Promise<void>
  onChallengeAdvance: () => void
  onChallengeStart: () => void
  onAnswer: (data: {
    uid: string
    answer: any
    answerIsCorrect?: boolean
    skipValidation?: boolean
    voucherId?: number
  }) => Promise<void>
  onLooseHeart: () => void
  onChallengeRetry: (data: { keepAnswer?: boolean }) => void
  goToOutOfTimeScreen: () => void
  goToOutOfHeartsScreen: () => void
  startCountdown: () => void
  pauseCountdown: () => void
  loadWrapperConfig: {
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
  }
  children: React.ReactNode
}

const LevelContext = createContext<LevelContextType | null>(null)

export const useLevelContext = () => {
  const context = useContext(LevelContext)

  if (!context) {
    throw new Error('useLevelContext must be used within a LevelProvider')
  }

  return context
}

export const LevelProvider: React.FC<{
  children: React.ReactNode
  digitalProductId: string
  id: string
  config: any
}> = ({ digitalProductId, id, config, children }) => {
  const contextValue = useLevel({
    digitalProductId,
    id,
    config
  })

  if (!contextValue.level) {
    // @ts-expect-error type component
    return <LoadWrapper {...contextValue.loadWrapperConfig} fullscreen />
  }

  const { level, digitalProduct, loadWrapperConfig } = contextValue

  return (
    // @ts-expect-error type component
    <LoadWrapper {...loadWrapperConfig} fullscreen>
      <SiteMeta
        defaultTitle={`${level?.name} - ${level?.journey?.name} | ${digitalProduct?.name}`}
        titleTemplate={`%s | ${digitalProduct?.name}`}
        faviconHref={_.get(
          digitalProduct,
          'favicon.file.url',
          '/placeholders/icon.png'
        )}
      />
      {/* <meta name="viewport" content="width=device-width, user-scalable=no" />
        {level && <meta name="description" content={level.description} />} */}
      {/* Sem caixa intermediária: a tela de execução é a página. A raiz de
          altura de viewport e a coluna flex moram no `LevelPlay.Screen`, que os
          filhos montam. A camada de imagem de fundo do stage saiu junto com o
          card — ela só aparecia na margem em volta dele, e no full-bleed não há
          margem onde exibi-la. O campo segue existindo na API e em outras telas. */}
      <LevelContext.Provider value={contextValue as LevelContextType}>
        {/* Superfície de jogo (D4c): é o que os kinds consomem. O contexto de
            domínio acima segue existindo para shell e telas, que precisam do
            registro do nível. */}
        <LevelPlaySurfaceProvider
          surface={{
            onComplete: contextValue.onChallengeComplete,
            onContinue: contextValue.onChallengeContinue,
            onAnswer: contextValue.onAnswer,
            loseHeart: contextValue.loseHeart,
            setStepsCustomFooters: contextValue.setStepsCustomFooters,
            isSubmitting: contextValue.isSubmitting,
            disableInputs: contextValue.disableInputs,
            answerIsWrong: contextValue.answerIsWrong,
            answerIsRight: contextValue.answerIsRight
          }}
        >
          {children}
        </LevelPlaySurfaceProvider>
      </LevelContext.Provider>
    </LoadWrapper>
  )
}

export const withLevel = (Component: React.ComponentType<any>) => {
  const WrappedComponent = (componentProps: {
    digitalProductId: string
    id: string
  }) => {
    const config = getConfig(componentProps)

    return (
      <LevelProvider config={config} {...componentProps}>
        <Component {...componentProps} />
      </LevelProvider>
    )
  }

  WrappedComponent.displayName = `withLevel(${
    Component.displayName || Component.name
  })`

  return WrappedComponent
}
